import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useActivation } from "@/contexts/ActivationContext";
import NotificationBell from "@/components/NotificationBell";
import {
  GaugeIcon,
  TruckIcon,
  WrenchIcon,
  CalendarCheckIcon,
  WarningIcon,
  ChartBarIcon,
  UsersThreeIcon,
  SignOutIcon,
  UserCircleIcon,
  RobotIcon,
  SteeringWheelIcon,
  PackageIcon,
  StorefrontIcon,
  MapTrifoldIcon,
  PlugsIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ClipboardTextIcon,
  GearSixIcon,
  RoadHorizonIcon,
  CaretDownIcon,
  CaretUpIcon,
  ChatCircleTextIcon,
  MapPinAreaIcon,
  BellIcon,
  GasPumpIcon,
} from "@phosphor-icons/react";

export default function Layout() {
  const { user, logout, checking: authChecking } = useAuth();
  const { activated, loading: activationLoading } = useActivation();
  const nav = useNavigate();
  const location = useLocation();
  // Company admins whose company hasn't paid yet — force them to Settings only.
  // Wait until BOTH auth and activation contexts have resolved before redirecting.
  const needsActivation = !authChecking && !activationLoading && !activated && user?.role !== "super_admin";
  useEffect(() => {
    if (needsActivation && !location.pathname.startsWith("/settings")) {
      nav("/settings", { replace: true });
    }
  }, [needsActivation, location.pathname, nav]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("fleet_sidebar_collapsed") === "1"; } catch (e) { return false; }
  });
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const raw = localStorage.getItem("fleet_sidebar_sections");
      if (raw) return JSON.parse(raw);
    } catch {}
    return { dispatch: true, repair: false, user: false, settings: false };
  });
  const toggleSection = (id) => {
    setExpandedSections((s) => {
      const next = { ...s, [id]: !s[id] };
      try { localStorage.setItem("fleet_sidebar_sections", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    try {
      localStorage.setItem("fleet_sidebar_collapsed", collapsed ? "1" : "0");
    } catch (e) {
      // localStorage may be blocked in private mode — sidebar preference
      // is non-critical so we silently ignore.
    }
  }, [collapsed]);

  const isMechanic = user?.role === "mechanic";
  const isAdmin = user?.role === "admin";

  // 4 top-level sidebar sections
  const nav_sections = isMechanic
    ? [
        {
          id: "repair",
          label: "Repair",
          icon: WrenchIcon,
          items: [
            { to: "/jobs", label: "My Jobs", icon: ClipboardTextIcon, end: true, tid: "nav-jobs" },
            { to: "/trucks", label: "Fleet", icon: TruckIcon, tid: "nav-trucks" },
            { to: "/trailers", label: "Trailers", icon: PackageIcon, tid: "nav-trailers" },
            { to: "/repairs", label: "Repairs", icon: WrenchIcon, tid: "nav-repairs" },
            { to: "/services", label: "Services", icon: CalendarCheckIcon, tid: "nav-services" },
            { to: "/inventory", label: "Inventory", icon: PackageIcon, tid: "nav-inventory" },
            { to: "/alerts", label: "Alerts", icon: WarningIcon, tid: "nav-alerts" },
          ],
        },
      ]
    : [
        {
          id: "dispatch",
          label: "Dispatch",
          icon: RoadHorizonIcon,
          items: [
            { to: "/app", label: "Dashboard", icon: GaugeIcon, end: true, tid: "nav-dashboard" },
            { to: "/fleet-map", label: "Live Map", icon: MapPinAreaIcon, tid: "nav-fleet-map" },
            { to: "/messages", label: "Messages", icon: ChatCircleTextIcon, tid: "nav-messages" },
            { to: "/geofences", label: "Geofences", icon: MapPinAreaIcon, tid: "nav-geofences" },
          ],
        },
        {
          id: "repair",
          label: "Repair",
          icon: WrenchIcon,
          items: [
            { to: "/trucks", label: "Fleet", icon: TruckIcon, tid: "nav-trucks" },
            { to: "/trailers", label: "Trailers", icon: PackageIcon, tid: "nav-trailers" },
            { to: "/jobs", label: "Jobs", icon: ClipboardTextIcon, tid: "nav-jobs" },
            { to: "/repairs", label: "Repairs", icon: WrenchIcon, tid: "nav-repairs" },
            { to: "/services", label: "Services", icon: CalendarCheckIcon, tid: "nav-services" },
            { to: "/alerts", label: "Alerts", icon: WarningIcon, tid: "nav-alerts" },
            { to: "/inventory", label: "Inventory", icon: PackageIcon, tid: "nav-inventory" },
            { to: "/repair-shops", label: "Repair Shops", icon: StorefrontIcon, tid: "nav-shops" },
            { to: "/fuel-prices", label: "Fuel Prices", icon: GasPumpIcon, tid: "nav-fuel-prices" },
            { to: "/reports", label: "Reports", icon: ChartBarIcon, tid: "nav-reports" },
            { to: "/ai", label: "Rep Fleet AI", icon: RobotIcon, tid: "nav-ai" },
          ],
        },
        {
          id: "user",
          label: "User",
          icon: UsersThreeIcon,
          items: [
            { to: "/drivers", label: "Drivers", icon: SteeringWheelIcon, tid: "nav-drivers" },
            ...(isAdmin ? [{ to: "/users", label: "Users", icon: UsersThreeIcon, tid: "nav-users" }] : []),
          ],
        },
        ...(isAdmin ? [{
          id: "settings",
          label: "Settings",
          icon: GearSixIcon,
          items: [
            { to: "/integrations", label: "Integrations", icon: PlugsIcon, tid: "nav-integrations" },
            { to: "/settings", label: "Settings", icon: GearSixIcon, tid: "nav-settings" },
          ],
        }] : []),
      ];

  // Gate the sidebar down to just Settings while the company is not activated
  const gated_sections = needsActivation
    ? [{
        id: "settings",
        label: "Get started",
        icon: GearSixIcon,
        items: [
          { to: "/settings", label: "Activate subscription", icon: GearSixIcon, tid: "nav-settings" },
        ],
      }]
    : nav_sections;

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`bg-slate-900 text-slate-200 flex flex-col fixed h-screen z-30 transition-transform duration-300 ease-in-out ${collapsed ? "-translate-x-full" : "translate-x-0"}`}
        style={{ width: "16rem" }}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        <div className="px-6 py-6 border-b border-slate-800">
          <Link to="/app" className="flex items-center gap-3" data-testid="brand-logo-link">
            <img src="/logo.png" alt="Rep Fleet" className="w-11 h-11 rounded-sm bg-white p-0.5 object-contain" />
            <div>
              <div className="font-display font-black text-lg tracking-tighter text-white leading-none">REP FLEET</div>
              <div className="font-mono text-[10px] text-blue-400 uppercase tracking-widest mt-1">{user?.company_name || "Fleet OS"}</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
          {gated_sections.map((sec) => {
            const SecIcon = sec.icon;
            // Auto-expand when the user has only ONE section (e.g. mechanic sees
            // only Repair) — otherwise the collapsed default hides every link.
            const isOpen = needsActivation || gated_sections.length === 1 ? true : !!expandedSections[sec.id];
            return (
              <div key={sec.id} className="mb-1">
                <button
                  type="button"
                  onClick={() => toggleSection(sec.id)}
                  data-testid={`nav-section-${sec.id}`}
                  className="w-full flex items-center justify-between px-6 py-2 text-[11px] uppercase tracking-[0.18em] font-mono font-bold text-slate-400 hover:text-white transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <SecIcon size={13} weight="bold" />
                    {sec.label}
                  </span>
                  {isOpen ? <CaretUpIcon size={12} weight="bold" /> : <CaretDownIcon size={12} weight="bold" />}
                </button>
                {isOpen && (
                  <div className="pl-2">
                    {sec.items.map((it) => {
                      const Icon = it.icon;
                      return (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          end={it.end}
                          data-testid={it.tid}
                          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                        >
                          <Icon size={16} weight="bold" />
                          <span>{it.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-800 p-4">
          <div className="flex items-center gap-3 mb-3" data-testid="sidebar-user-info">
            <div className="w-9 h-9 rounded-sm bg-slate-800 flex items-center justify-center">
              <UserCircleIcon size={22} className="text-slate-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 py-2 rounded-sm transition-colors duration-150 font-medium"
          >
            <SignOutIcon size={16} weight="bold" /> Sign out
          </button>
        </div>
      </aside>

      {/* Sliding toggle button — sits on the edge of the sidebar */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        data-testid="sidebar-toggle"
        aria-label={collapsed ? "Open sidebar" : "Close sidebar"}
        title={collapsed ? "Open sidebar" : "Close sidebar"}
        className="fixed top-1/2 -translate-y-1/2 z-40 w-7 h-16 bg-slate-900 hover:bg-amber-500 text-amber-400 hover:text-slate-900 rounded-r-md shadow-lg flex items-center justify-center transition-all duration-300 ease-in-out"
        style={{ left: collapsed ? "0" : "16rem" }}
      >
        {collapsed ? <CaretRightIcon size={16} weight="bold" /> : <CaretLeftIcon size={16} weight="bold" />}
      </button>

      {/* Main */}
      <main
        className="flex-1 min-h-screen transition-all duration-300 ease-in-out"
        style={{ marginLeft: collapsed ? "0" : "16rem" }}
      >
        {needsActivation && (
          <div className="bg-amber-500 text-slate-900 px-6 py-3 border-b-2 border-amber-600 flex items-center gap-3" data-testid="activation-banner">
            <WarningIcon size={22} weight="fill" className="flex-shrink-0" />
            <div className="flex-1">
              <div className="font-display font-black text-sm tracking-tight">Activate your subscription to unlock Rep Fleet</div>
              <div className="text-xs mt-0.5">
                Choose a plan below to start your <b>14-day free trial</b>. All features unlock as soon as your card is on file — no charge until day 15.
              </div>
            </div>
          </div>
        )}
        {!isMechanic && !needsActivation && <NotificationBell />}
        <Outlet />
      </main>
    </div>
  );
}
